import checkCurrDomainIsOrgDomain from "../helpers/checkCurrDomainIsOrgDomain";

import { useEffect } from "react";
import { GetUploadEndpointApiRequest } from "../api/MailsController";
import { DOMAIN_NAME, JUMBOMAIL_BUCKET_NAME } from "../utils/globalConsts";
import type { UserDataT } from "models/models";
import { CustomDomainPagesE, MainDomainPagesE } from "models/enums";
import { useUser } from "../contexts/userContext/userContext";

// Domain (D) — represents the value of window > location > hostname.
// Main domain (MD) — domain, which HAS included  “jumbomail.me” OR “jmbl.me” values.
// Custom domain (CD) — domain, which does NOT include “jumbomail.me” OR “jmbl.me” values.
// Usual User (UU) — user, registered NOT under organization.
// Top User (TU) — organization owner.
// Sub User (SU) — user, which IS registered under organization as sub user.
// Organization User (OU) — TU OR SU.
// Bucket (B) — AWS S3 bucket to which mail was uploaded.

// Scenarios:
// Here you can see list of possible cases with different scenarios for all involved pages:

// https://docs.google.com/spreadsheets/d/1kty4yzg4Qm0PBWSdV5YJnr5kBIfD-EcI3XOPrOplgmw/edit?gid=0#gid=0

// Types

// Definitions

const DEFAULT_LANG = "en";
const MAIL_ID_PATH_INDEX = 3;
const MAIN_DOMAIN_ORIGIN = `https://${DOMAIN_NAME}`;

const domain = window.location.hostname;
const currOrigin = window.location.origin;
const currPath = window.location.pathname;
const isLocalhost = domain.includes("localhost");

// Helpers

/**
 * Checks if the current window.location.hostname value includes any of  “jumbomail.me” or “jmbl.me” values.
 * The returned value is the 2nd column from the scenario list.
 *
 * We could do strict comparison for the full string, but in the future we need to support
 * “test.jumbomail.me”, “test2.jumbomail.me”, “www.jumbomail.me”, and perhaps something else.
 *
 * @returns TRUE or FALSE.
 *
 * Note: there is IS_LOCALHOST_TREATED_AS_CUSTOM_DOMAIN constant, which can be set as TRUE or FALSE.
 * If it is set to TRUE, “localhost” will be treated as a CD as well.
 * This is needed for developers purposes only.
 */
const _checkIsCurrDomainIsCustom = () => {
  const isCustomDomain = checkCurrDomainIsOrgDomain();

  return isCustomDomain;
};

/**
 * Return current page name (1st column from the scenario list) from the window.location.pathname value.
 * It splits the value by “/” separator and gets the 3rd element from the received array.
 * If the element exists, it returns it in lower case.
 *
 * Therefore, “https://[CD]/[lang]/login/” URL will return “login” page value.
 */
const _getCurrentPage = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, lang, page] = currPath.split("/");

  if (page) {
    const pageName = page.toLowerCase();

    return pageName as CustomDomainPagesE;
  } else {
    return undefined;
  }
};

/**
 *  There are few pages allowed to be seen without need to be logged in under the CD:
 * 
    gallery
    collection
    j
    page
    sub-user-complete 
    invite

    The helpers check if the current page belongs to the one from the list and returns TRUE, 
    if the page is not in the “allowed pages” list (so, it means page is restricted), and FALSE otherwise. 

    We need this logic to check if we need to redirect to the “login” page,
    which we can see in the last column of the scenario list.
*/
const _checkPageIsRestrictedForNotLoggedInUserUnderCustomDomain = (
  page: CustomDomainPagesE | MainDomainPagesE
) => {
  const allowedPages = [
    CustomDomainPagesE.GALLERY,
    CustomDomainPagesE.COLLECTION,
    CustomDomainPagesE.PAGE,
    CustomDomainPagesE.SUB_USER_COMPLETE,
    CustomDomainPagesE.INVITE,
    CustomDomainPagesE.REGISTRATION_COMPLETE,
    CustomDomainPagesE.LOGIN,
    CustomDomainPagesE.PASSWORD_RECOVER,
    CustomDomainPagesE.USER_CONFIRMATION,
    CustomDomainPagesE.EMBED_DYNAMICS,
    CustomDomainPagesE.FORM,

    // Main domain pages need to be not restricted in order to make redirect to main domain from them
    MainDomainPagesE.ERROR,
    MainDomainPagesE.SUBSCRIBERS,
    MainDomainPagesE.PLANS,
    MainDomainPagesE.JUMBO_FORMS,
    MainDomainPagesE.J,
    MainDomainPagesE.REGISTRATION,
    MainDomainPagesE.FOR_BUSINESSES,
    MainDomainPagesE.JUMBO_PICKER,
    MainDomainPagesE.ABOUT,
    MainDomainPagesE.UPLOAD_SPEED_TEST,
    MainDomainPagesE.USER_CONFIRMATION,
    MainDomainPagesE.RESET_PASSWORD,
    MainDomainPagesE.CODES,
    MainDomainPagesE.EMAIL_PREFERENCES,
    MainDomainPagesE.JUMBO_PAY,
    MainDomainPagesE.CONTACT_US,
    MainDomainPagesE.SUPPORT,
    MainDomainPagesE.FEATURES,
    MainDomainPagesE.BLOG,
    MainDomainPagesE.BLOG_ARTICLE,
    MainDomainPagesE.NEWS,
    MainDomainPagesE.STAGE,
    MainDomainPagesE.STAGE_CREATOR,
    MainDomainPagesE.PURCHASE,
    MainDomainPagesE.TERNS,
    MainDomainPagesE.ACCOUNT_DELETED,
    MainDomainPagesE.PURCHASE_ERROR_RETRY,
    MainDomainPagesE.AUTHENTICATE_LOGIN,
    MainDomainPagesE.AUTHENTICATE_USER,
  ];

  const isPageAllowed = allowedPages.includes(page as CustomDomainPagesE);

  return !isPageAllowed;
};

/**
 * Take current language as the argument and redirect to the login page under current D.
 */
const _redirectToLogin = () => {
  const currPage = _getCurrentPage();

  // Add console log in order to see from where redirect was made
  console.warn(
    "useCustomDomainRedirect.ts, line 155: Redirect to login from " + currPage
  );

  window.location.href = `${currOrigin}/${DEFAULT_LANG}/login`;
};

/**
 * It adds the protocol to the bucket name and removes “files.” prefix from the bucket name if it exists.
 */
const _makeOriginFromBucketName = (bucketName: string) => {
  if (!bucketName) {
    return currOrigin;
  }

  const protocol = window.location.protocol;
  const sanitizedBucketName = bucketName.replace("files.", "");

  // localhost - do not redirect to the bucket
  // todo - redirect to special custom test domain bucket
  if (isLocalhost) {
    return currOrigin;
  }

  if (sanitizedBucketName.includes(JUMBOMAIL_BUCKET_NAME)) {
    if (
      isLocalhost ||
      currOrigin.includes("test") ||
      currOrigin.includes("test2")
    ) {
      return currOrigin;
    }

    return MAIN_DOMAIN_ORIGIN;
  }

  const origin = `${protocol}//${sanitizedBucketName}`;

  return origin;
};

/**
 * It takes the mailId argument and fetches the bucket name with the passed mail id.
 * If successful, it builds the origin string with _makeOriginFromBucketName helper and returns it.
 */
const _getOriginByEncryptedMailId = async (encryptedMailId: string) => {
  const response = await GetUploadEndpointApiRequest({
    encryptedMailId,
  });

  if (response.status === "success") {
    const bucketName = response.data;
    const origin = _makeOriginFromBucketName(bucketName);
    return origin;
  }
};

/**
 * The helper parses URl and finds the mailId.
 */
const _getMailIdFromGalleryPagePath = () => {
  const encryptedMailId = currPath.split("/")[MAIL_ID_PATH_INDEX];

  if (!encryptedMailId) {
    return undefined;
  }

  return encryptedMailId;
};

const useCustomDomainRedirect = () => {
  const { isLoggedIn, getIsPendingLogin, getUserData } = useUser();
  const user = getUserData() as UserDataT;
  const isCustomDomain = _checkIsCurrDomainIsCustom();

  const userOrigin = user?.BucketName;

  /**
   * Get Page Origin
    This is the root function, where the general logic is described. It takes the page name,
    isLoggedIn parameter and isCurrDomainCustom parameter. 
    Using the “switch” operator, we check each page if it needs to be redirected to the CD/MD.

    The first parameter accepts values of CD pages and MD pages.

    The logic for pages from MainDomainPagesE list is simple:

    If the user (no matter UU or SU/OU) staying under CD — do redirect to MD.
    If the current domain is MD — do nothing.

    But for CD pages (CustomDomainPagesE list) a little bit complex and depend on page: 
    for some pages (such as GALLERY or COLLECTION) domain depends on mail bucket mail, 
    for another (such as PAGE page), need to make additional request to fetch domain data, etc. 

    Also, if the current CD is not equal to CD from user data, 
    it will substitute current with the user one. Everything is described below.

    Note: logic for PAGE case implemented in the src\components\Pages\NewEmbedPage\NewEmbedPage.js file, 
    using useBucketName hook. because it is not possible to get the pageOwnerUserId from the URL
    and we need a double request for it.
    It is possible to pass this value to the global state, but need to do it after full migration to redux.

  */
  const getPageOrigin = async (
    page: CustomDomainPagesE | MainDomainPagesE,
    isLoggedIn: boolean,
    isCurrDomainCustom: boolean
  ) => {
    if (currPath.startsWith("/j/")) {
      return currOrigin;
    }

    switch (page) {
      case CustomDomainPagesE.REGISTRATION_COMPLETE: {
        return currOrigin;
      }
      case CustomDomainPagesE.HOME:
      case CustomDomainPagesE.INBOX:
      case CustomDomainPagesE.USER:
      case CustomDomainPagesE.PAGE_EDIT:
      case CustomDomainPagesE.UPLOAD:
      case CustomDomainPagesE.CREATE_FORM:
      case CustomDomainPagesE.ORDER_TRACKER:
      case CustomDomainPagesE.FORM:
      case CustomDomainPagesE.PROFILE:
      case CustomDomainPagesE.PICKER_SUCCESS: {
        if (isLoggedIn && !isCurrDomainCustom) {
          return userOrigin;
        }
        if (isLoggedIn && isCurrDomainCustom && currOrigin !== userOrigin) {
          return userOrigin;
        }
        return currOrigin;
      }
      case CustomDomainPagesE.J: {
        return currOrigin;
      }
      case CustomDomainPagesE.USER_CONFIRMATION: {
        return currOrigin;
      }
      case CustomDomainPagesE.LOGIN:
      case CustomDomainPagesE.PASSWORD_RECOVER: {
        return currOrigin;
      }
      case CustomDomainPagesE.GALLERY:
      case CustomDomainPagesE.COLLECTION: {
        const encryptedMailId = _getMailIdFromGalleryPagePath();

        if (!encryptedMailId) {
          return currOrigin;
        }

        const mailOrigin = await _getOriginByEncryptedMailId(encryptedMailId);

        if (
          (currOrigin.includes("amplify") || currOrigin.includes("staging")) &&
          mailOrigin.includes("jumbomail")
        ) {
          return currOrigin;
        }

        return mailOrigin;
      }
      case CustomDomainPagesE.SUB_USER_COMPLETE:
      case CustomDomainPagesE.INVITE: {
        // This logic implemented in the relevant pages
        // because we need to fetch Bucket name from ReactGetSubUserDataByLink
        // and ReactGetSubUserDataByEmail APIrequest.
        return currOrigin;
      }
      case CustomDomainPagesE.PAGE: {
        // This logic implemented in the src\components\Pages\NewEmbedPage\NewEmbedPage.js file
        // because it is not possible to get the pageOwnerUserId from the URL and we need double request for it.
        // It is possible to pass this value to the global state, but need to do it after full migration to redux.
        return currOrigin;
      }

      case MainDomainPagesE.AUTHENTICATE_LOGIN: {
        // RETURN CURRENT ORIGIN FOR AUTHENTICATE_LOGIN PAGE
        // DUE TO BEARER TOKEN IS NOT VALID FOR DEFAULT DOMAIN
        return currOrigin;
      }

      // Main domain pages
      default: {
        const isCurrDomainCustom = _checkIsCurrDomainIsCustom();

        if (isCurrDomainCustom) {
          return MAIN_DOMAIN_ORIGIN;
        }

        return currOrigin;
      }
    }
  };

  /**
   * This function uses the result of the getOriginPage and redirects to a new origin if needed.

    If the user is not logged in, it checks (with _checkPageIsRestrictedForNotLoggedInUserUnderCustomDomain helper)
    if it is needed to redirect to the login page (using redirectToLogin helper) from the restricted page. 
    This logic works if the user went on this page under a CD.
  */
  const redirectToCorrectDomain = (
    newOrigin: string,
    isLoggedIn: boolean,
    isPendingLogin: boolean
  ) => {
    const isCustomDomain = _checkIsCurrDomainIsCustom();
    const currPage = _getCurrentPage();
    const isPageRestrictedForNotLoggedInUser =
      _checkPageIsRestrictedForNotLoggedInUserUnderCustomDomain(currPage);

    // Don't allow to see restricted pages for not logged in users under CD
    if (
      isCustomDomain &&
      !isLoggedIn &&
      !isPendingLogin &&
      isPageRestrictedForNotLoggedInUser &&
      !window.location.pathname.startsWith("/j/")
    ) {
      _redirectToLogin();
      return;
    }

    // Do nothing if the origin is the same
    if (currOrigin === newOrigin) {
      return;
    }

    // Substituting the current origin with the new one
    window.location.href = `${newOrigin}${currPath}`;
  };

  // Wrap the logic in the async function and call it in the useEffect hook
  // because it is not allowed to use async in the useEffect hook directly.
  const asyncWrapper = async () => {
    const currentPage = _getCurrentPage();

    if (!currentPage) {
      return;
    }

    const pageOrigin = await getPageOrigin(
      currentPage,
      isLoggedIn(),
      isCustomDomain
    );

    if (!pageOrigin) {
      return;
    }

    redirectToCorrectDomain(pageOrigin, isLoggedIn(), getIsPendingLogin());
  };

  useEffect(() => {
    asyncWrapper();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomDomain, isLoggedIn()]);

  return {
    // ========>>>>>>>> ENTRY POINT <<<<<<<<==========
    checkRedirect: asyncWrapper,
  };
};

export default useCustomDomainRedirect;
